import React, { useState, useRef, useEffect, useCallback } from "react";
import { CircleAlert, Pin } from "lucide-react";
import { EventResponse, PinRequest, Post, PostComment, Reaction } from "types/Post";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import { useTranslation } from "react-i18next";
import { CommentsSection } from "./CommentsSection";
import { ImageSlider } from "./ImageSlider";
import { ReactionPeopleDropdown } from "./ReactionPeopleDropdown";
import { ReactionPills } from "./ReactionPills";
import { EventSection } from "./EventSection";
import { AcknowledgementSection } from "./AcknowledgementSection";
import { PinDialog } from "./PinDialog";
import { UserNameWithCompany } from "./UserNameWithCompany";
import { TOUCH_TARGET, TOUCH_TARGET_PILL } from "./touchTargets";
import { formatDate } from "../utils/commentUtils";
import { sanitizeHTML } from "../utils/sanitize";

interface PostCardProps {
  post: Post;
  /** The company whose feed page this is (not necessarily the post's company). */
  companyId: string;
  /** Viewer is an enabled Master of the page's company. */
  isMaster: boolean;
  /** Viewer organises events and pins posts in the page's company (Master or "Access onboarding"). */
  canOrganize: boolean;
  /** Viewer is a member of the page's company; a visitor of the group never edits or deletes posts. */
  canManagePosts: boolean;
  /** Give the reaction to the post or take it back; the page shows the result and reports a failure itself. */
  onReact: (postId: string, reaction: Reaction) => Promise<void>;
  onComment: (postId: string, comment: string, parent?: string) => Promise<PostComment>;
  onDeleteComment?: (postId: string, commentId: string) => Promise<void>;
  onUpdateComment?: (postId: string, commentId: string, comment: string) => Promise<PostComment>;
  onDeletePost?: (postId: string) => Promise<void>;
  onEditPost?: (post: Post) => void;
  /** Answer the post's event; `null` takes the answer back. */
  onEventResponse: (postId: string, response: EventResponse | null) => Promise<void>;
  /** Pin or unpin the post; the page reports a failed request itself. */
  onPin: (postId: string, request: PinRequest) => Promise<void>;
  /** The viewer acknowledges the pinned post; the page reports a failed request itself. */
  onAcknowledge: (postId: string) => Promise<void>;
  getFileUrl: (fileId: string) => Promise<string>;
}

/** DOM id of a post's card, so the page can scroll to it. */
export const postElementId = (postId: string) => `post-${postId}`;

export function PostCard({
  post,
  companyId,
  isMaster,
  canOrganize,
  canManagePosts,
  onReact,
  onComment,
  onDeleteComment,
  onUpdateComment,
  onDeletePost,
  onEditPost,
  onEventResponse,
  onPin,
  onAcknowledge,
  getFileUrl,
}: PostCardProps) {
  const { t, i18n } = useTranslation();
  const { session } = useSelector((state: RootState) => state.authReducer);
  const [isReacting, setIsReacting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.commentsCount);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [showReactionPeople, setShowReactionPeople] = useState(false);
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [showImageSlider, setShowImageSlider] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const whoReactedButtonRef = useRef<HTMLButtonElement>(null);
  const postCardRef = useRef<HTMLDivElement>(null);

  // A post of another company of the group can be read, reacted to and commented here, but only edited on its own company's page.
  const isPostOfThisCompany = post.company === companyId;
  const isOwnPost = session?.id === post.user?.id;
  const canManagePost = canManagePosts && isPostOfThisCompany && (isOwnPost || isMaster);
  // Pins belong to the post's own company: its organisers pin, whoever wrote the post.
  const canPin = canOrganize && isPostOfThisCompany;
  const hasReactions = post.reactions.some((summary) => summary.count > 0);
  const userName = post.user?.name || t("feed_user");
  const userInitial = userName.charAt(0).toUpperCase();

  const [isDetailsShown, setIsDetailsShown] = useState(false);

  // Sync state with props when post changes
  useEffect(() => {
    setCommentsCount(post.commentsCount);
  }, [post.commentsCount, post.id]);

  useEffect(() => {
    // Load file URLs
    const loadFileUrls = async () => {
      if (post.files && post.files.length > 0) {
        setLoadingFiles(true);
        const urls: { [key: string]: string } = {};
        try {
          await Promise.all(
            post.files.map(async (file) => {
              try {
                const url = await getFileUrl(file.id);
                urls[file.id] = url;
              } catch (error) {
              }
            })
          );
          setFileUrls(urls);
        } catch (error) {
          console.error("Error loading file URLs:", error);
        } finally {
          setLoadingFiles(false);
        }
      } else {
        setLoadingFiles(false);
      }
    };
    loadFileUrls();

    // Cleanup blob URLs on unmount
    return () => {
      setFileUrls((currentUrls) => {
        Object.values(currentUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
    };
  }, [post.files, getFileUrl]);

  // The pills stay off until the page has the answer: two answers arriving out of order would leave the wrong counts.
  const handleReact = async (reaction: Reaction) => {
    setIsReacting(true);
    try {
      await onReact(post.id, reaction);
    } finally {
      setIsReacting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!onDeletePost) return;
    setIsDeleting(true);
    try {
      await onDeletePost(post.id);
      // Component will likely unmount, but reset state for safety
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting post:", error);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Close menu when clicking outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  }, []);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showMenu, handleClickOutside]);

  return (
    <div id={postElementId(post.id)} data-testid="post-card" ref={postCardRef} className={`bg-white rounded-lg shadow-sm border ${post.pin ? "border-amber-300" : "border-gray-200"} p-4 mb-4 hover:shadow-md transition-shadow relative scroll-mt-4`}>
      {(post.isImportant || post.pin) && (
        <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="post-badges">
          {post.isImportant && (
            <div className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              <CircleAlert className="h-3 w-3" />
              {t("news_important_badge")}
            </div>
          )}
          {post.pin && (
            <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              <Pin className="h-3 w-3" />
              {t("news_pinned_badge")}
            </div>
          )}
        </div>
      )}
        {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {userInitial}
          </div>
          <div>
            <div className="font-semibold text-gray-900">
              <UserNameWithCompany
                name={userName}
                companyName={post.companyName}
                companyId={post.company}
                ownCompanyId={companyId}
              />
            </div>
            <div className="flex items-center space-x-1 text-sm text-gray-500">
              <span>{formatDate(post.createdAt, i18n, t)}</span>
              {post.updatedAt && post.updatedAt > post.createdAt && (
                <span className="text-xs text-gray-500 italic whitespace-nowrap">
                  · {t("feed_edited")}
                </span>
              )}
            </div>
          </div>
        </div>
        {(canManagePost || canPin) && (
          <div className="relative" ref={menuRef}>
            {showDeleteConfirm ? (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDeletePost}
                  disabled={isDeleting}
                  className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                >
                  {isDeleting ? t("feed_deleting") : t("feed_confirm_delete")}
                </button>
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="text-gray-600 hover:text-gray-700 text-sm disabled:opacity-50"
                >
                  {t("feed_cancel")}
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className={`flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ${TOUCH_TARGET}`}
                  aria-label={t("feed_more_options")}
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    {canPin && (
                      <button
                        onClick={() => {
                          if (post.pin) onPin(post.id, { pinned: false });
                          else setShowPinDialog(true);
                          setShowMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        {post.pin ? t("news_unpin_action") : t("news_pin_action")}
                      </button>
                    )}
                    {canManagePost && (
                      <>
                        <button
                          onClick={() => {
                            if (onEditPost) {
                              onEditPost(post);
                            }
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          {t("feed_edit")}
                        </button>
                        <button
                          onClick={() => {
                            handleDeleteClick();
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                        >
                          {t("feed_delete")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {post.event && (
        <EventSection event={post.event} onRespond={(response) => onEventResponse(post.id, response)} />
      )}

      {/* Content */}
      <div className="mb-3 text-gray-900 break-words prose prose-sm max-w-none">
        <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.content || "") }} />

        {post.details && isDetailsShown && (
          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.details) }} />
        )}

        {post.details && (
          <button
            onClick={() => setIsDetailsShown(!isDetailsShown)}
            className="mt-1 text-sm text-blue-600 hover:underline"
          >
            {isDetailsShown ? t("feed_show_less") : t("feed_show_more")}
          </button>
        )}
      </div>
      {/* Images */}
      {post.files && post.files.length > 0 && (
        <div className="mb-3">
          {loadingFiles ? (
            <div className="h-64 bg-gray-200 rounded-lg animate-pulse"></div>
          ) : (
            <div className={`grid gap-2 ${
              post.files.length === 1 ? "grid-cols-1" : "grid-cols-2"
            }`}>
              {post.files.slice(0, 2).map((file, index) => (
                <div 
                  key={file.id} 
                  className="relative group cursor-pointer"
                  onClick={() => {
                    if (post.files && post.files.length > 0) {
                      setCurrentImageIndex(index);
                      setShowImageSlider(true);
                    }
                  }}
                >
                  {fileUrls[file.id] ? (
                    <>
                      <img
                        src={fileUrls[file.id]}
                        alt={file.title || t("feed_post_image", { index: index + 1 })}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      {index === 1 && post.files && post.files.length > 2 && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                          <div className="text-white text-4xl font-bold">
                            +{post.files.length - 2}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400">{t("feed_loading")}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Image Slider */}
      <ImageSlider
        files={post.files || []}
        fileUrls={fileUrls}
        isOpen={showImageSlider}
        initialIndex={currentImageIndex}
        onClose={() => setShowImageSlider(false)}
      />

      {post.pin?.requiresAck && (
        <AcknowledgementSection
          pin={post.pin}
          companyId={companyId}
          postId={post.id}
          isGroupPost={post.isGroupPost}
          canSeePending={isOwnPost || canPin}
          onAcknowledge={() => onAcknowledge(post.id)}
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-200">
        <div className="relative flex flex-wrap items-center gap-2">
          <ReactionPills reactions={post.reactions} onToggle={handleReact} isDisabled={isReacting} />
          {hasReactions && (
            <button
              ref={whoReactedButtonRef}
              type="button"
              onClick={() => setShowReactionPeople(!showReactionPeople)}
              className={`rounded px-2 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${TOUCH_TARGET_PILL}`}
            >
              {t("news_reactions_who")}
            </button>
          )}
          {showReactionPeople && (
            <ReactionPeopleDropdown
              companyId={companyId}
              postId={post.id}
              postCompanyId={post.company}
              openerRef={whoReactedButtonRef}
              onClose={() => setShowReactionPeople(false)}
            />
          )}
        </div>

        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <span className="font-medium">
            {commentsCount > 0 ? `${commentsCount} ${t("feed_comments")}` : t("feed_comment")}
          </span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <CommentsSection
          postId={post.id}
          companyId={companyId}
          initialComments={Array.isArray(post.comments) ? post.comments : []}
          initialCommentsCount={post.commentsCount}
          onComment={onComment}
          onDeleteComment={onDeleteComment}
          onUpdateComment={onUpdateComment}
          onCommentsCountChange={setCommentsCount}
          postCompanyId={post.company}
        />
      )}

      {showPinDialog && (
        <PinDialog
          onClose={() => setShowPinDialog(false)}
          onPin={(requiresAck) => onPin(post.id, { pinned: true, requiresAck })}
        />
      )}
    </div>
  );
}
